import React from "react";
import moment from "moment";
import clsx from "clsx";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import PlaybookReviewService from "../../services/PlaybookReviewService";
import PlaybookRunService from "../../services/PlaybookRunService";
import PlaybookInputFieldService from "../../services/PlaybookInputFieldService";
import AgentFindingCounts from "./AgentFindingCounts";

import "./agent-run-report.css";

const STOPPED_RUN_TITLES = {
	failed: "The run did not finish",
	interrupted: "The run stopped part-way",
	needs_linking: "The run is waiting for accounts to be linked",
};

class AgentRunRecord extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			showPasses: false,
		};
	}

	onTogglePasses = () => {
		this.update({ showPasses: !this.state.showPasses });
	};

	renderValuesUsed() {
		const { run } = this.props;
		const inputs = run.inputs || {};
		const fieldValues = inputs.fieldValues || {};
		const fieldKeys = Object.keys(fieldValues);
		const declaredFields = PlaybookInputFieldService.fieldsOf({ version: run.agent_version });
		const hasNothingToShow = declaredFields.length === 0 && fieldKeys.length === 0;
		if (hasNothingToShow) return null;
		const declaredKeys = declaredFields.map(field => field.key);
		const undeclaredKeysUsed = fieldKeys.filter(key => !declaredKeys.includes(key));
		const orderedKeys = [...declaredKeys, ...undeclaredKeysUsed];
		const valueLines = orderedKeys.map(key => {
			const declaredField = declaredFields.find(field => field.key === key);
			const field = declaredField || { key };
			const label = declaredField ? declaredField.label : key;
			const shownValue = PlaybookInputFieldService.describeFieldValue({ field, value: fieldValues[key] });
			return { key, label, shownValue };
		});
		return (
			<div className="agent-run-report__request">
				<span className="agent-run-report__request-label">Values used</span>
				<div className="agent-run-report__values">
					{valueLines.map(line => (
						<span key={line.key} className="agent-run-report__request-text">
							{line.label}: {line.shownValue}
						</span>
					))}
				</div>
			</div>
		);
	}

	renderSummary() {
		const { run } = this.props;
		const stoppedTitle = STOPPED_RUN_TITLES[run.status] || null;
		const isFailed = !!stoppedTitle;
		const request = PlaybookRunService.requestOfRun({ run });

		return (
			<div className="agent-run-report__summary">
				{isFailed && (
					<div className={clsx("tk-notice", run.status === "failed" ? "tk-notice--danger" : "tk-notice--warning")}>
						<div className="tk-notice__title">
							<Icon.AlertTriangle size={14} />
							{stoppedTitle}
						</div>
						<p className="tk-notice__detail">{run.error}</p>
					</div>
				)}
				{request && (
					<div className="agent-run-report__request">
						<span className="agent-run-report__request-label">Asked</span>
						<span className="agent-run-report__request-text">{request}</span>
					</div>
				)}
				{this.renderValuesUsed()}
				{!isFailed && <p className="agent-run-report__summary-text">{run.summary}</p>}
				{!isFailed && <AgentFindingCounts run={run} size="large" />}
			</div>
		);
	}

	renderNotChecked() {
		const { run } = this.props;
		const notChecked = run.not_checked || [];
		if (notChecked.length === 0) return null;
		return (
			<section className="agent-run-report__group">
				<div className="agent-run-report__group-head">
					<Icon.MinusCircle size={14} className="agent-run-report__group-icon" />
					<span className="agent-run-report__group-title">Not checked</span>
					<span className="agent-run-report__group-count">{notChecked.length}</span>
				</div>
				<div className="tk-card">
					{notChecked.map((item, index) => (
						<div key={index} className="agent-run-report__plain-row">
							<span className="agent-run-report__plain-title">{item.area}</span>
							<span className="agent-run-report__plain-detail">{item.reason}</span>
						</div>
					))}
				</div>
			</section>
		);
	}

	renderAccountsNeedingLinking() {
		const { review } = this.props;
		const unlinked = review ? review.accountsNeedingLinking : null;
		if (!unlinked) return null;
		if (!unlinked.linesRecorded) {
			return (
				<section className="agent-run-report__group">
					<p className="agent-run-report__plain-detail">This run was made before each amount's line was recorded, so accounts not linked to a line are not listed for it.</p>
				</section>
			);
		}
		if (unlinked.accounts.length === 0) return null;
		return (
			<section className="agent-run-report__group">
				<div className="agent-run-report__group-head">
					<Icon.Link2 size={14} className="agent-run-report__group-icon" />
					<span className="agent-run-report__group-title">Accounts not linked to a line</span>
					<span className="agent-run-report__group-count">{unlinked.accounts.length}</span>
				</div>
				<div className="tk-card">
					{unlinked.accounts.map(account => (
						<div key={`${account.kind}:${account.id}`} className="agent-run-report__plain-row">
							<span className="agent-run-report__plain-title">{account.name || `Account ${account.id}`}</span>
							<span className="agent-run-report__plain-detail">
								{account.kind === "bank"
									? "This bank account's Plaid connection names no QuickBooks account, so its transactions are not on a line."
									: "Not linked to the client's chart, so its amounts are not on a line. Link it in Bookkeeping's QuickBooks trial balance."}
							</span>
						</div>
					))}
				</div>
			</section>
		);
	}

	renderPasses() {
		const { run } = this.props;
		const { showPasses } = this.state;
		const passes = PlaybookRunService.passedChecks({ checks: run.checks });
		if (passes.length === 0) return null;
		return (
			<section className="agent-run-report__group">
				<button type="button" className="agent-run-report__toggle" onClick={this.onTogglePasses}>
					{showPasses ? <Icon.ChevronDown size={14} /> : <Icon.ChevronRight size={14} />}
					{passes.length} checks passed or looked at and found right
				</button>
				{showPasses && (
					<div className="tk-card">
						{passes.map((check, index) => (
							<div key={index} className="agent-run-report__plain-row">
								<Icon.Check size={13} className="agent-run-report__pass-icon" />
								<span className="agent-run-report__plain-detail">{check.statement}</span>
							</div>
						))}
					</div>
				)}
			</section>
		);
	}

	renderSignoffRecord() {
		const { review } = this.props;
		if (!review || review.signoffs.length === 0) return null;
		return (
			<section className="agent-run-report__group">
				<div className="agent-run-report__group-head">
					<Icon.Clipboard size={14} className="agent-run-report__group-icon" />
					<span className="agent-run-report__group-title">Sign-off record</span>
				</div>
				<div className="tk-card">
					{review.signoffs.map(signoff => (
						<div key={signoff.id} className={clsx("agent-run-report__plain-row", signoff.withdrawnAt && "agent-run-report__plain-row--withdrawn")}>
							<span className="agent-run-report__plain-title">
								{PlaybookReviewService.describeSignoffStage({ stage: signoff.stage })}
								{signoff.selfReviewed && " (self-reviewed)"}
							</span>
							<span className="agent-run-report__plain-detail">
								{signoff.by} · {moment(signoff.at).format("MMM D, h:mm a")}
								{signoff.note && ` — ${signoff.note}`}
								{signoff.attestation && <span className="agent-run-report__attestation">“{signoff.attestation}”</span>}
								{signoff.withdrawnAt && ` · withdrawn: ${signoff.withdrawnReason}`}
							</span>
						</div>
					))}
				</div>
			</section>
		);
	}

	render() {
		const { children } = this.props;
		return (
			<React.Fragment>
				{this.renderSummary()}
				{children}
				{this.renderAccountsNeedingLinking()}
				{this.renderNotChecked()}
				{this.renderPasses()}
				{this.renderSignoffRecord()}
			</React.Fragment>
		);
	}
}

export default AgentRunRecord;
